import React, { useState } from 'react';
import { BackButton, Size, ComponentM, ComponentTextStyle, HorizontalCard, SystemIcons, TextButton as Button, InputLabel, TextField } from '@laerdal/life-react-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StepContainer, GrayBox, StepTitle, StepDescription } from './StepComponents';
import BreadcrumbTopSection from '../../../../commonComponents/BreadcrumbTopSection';
import { selectMarket, buildMarketPath } from '../../../../store/slices/globalConfigSlice';
import posthog from 'posthog-js';



interface Step1Props {
  onNext: (step?: number) => void;
  handleResolvedStep: () => void;
  onPrevious: () => void;
  setAppVersion: (version: string) => void;
}

const Step1: React.FC<Step1Props> = ({ onNext, handleResolvedStep, onPrevious, setAppVersion }) => {
  const { t } = useTranslation(['translation', 'qcprSupport']);
  const currentMarket = useSelector(selectMarket);
  const [version, setVersion] = useState('');

  return (
    <StepContainer>
      <BreadcrumbTopSection
        setPadding={false}
        breadcrumb={[
          { text: t('Home', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/') },
          { text: t('Software and Apps', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps') },
          { text: t('QCPR App', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps/product/qcpr-app') },
          { text: t('Troubleshooting', { ns: 'qcprSupport' }) }
        ]}
      />

      <BackButton onClick={onPrevious} size={Size.Small}>
        {t('Back', { ns: 'qcprSupport' })}
      </BackButton>

      <StepTitle>{t('Did you update the app to the latest version?', { ns: 'qcprSupport' })}</StepTitle>
      <StepDescription>{t('We regularly fix issues with new versions of the app.', { ns: 'qcprSupport' })}</StepDescription>

      <HorizontalCard
        actions={[{
          componentType: 'icon',
          icon: <SystemIcons.OpenNewWindow />,
          action: () => {
            posthog.capture('SUPP-QcprSupport-AppStoreClicked', {
              platform: 'iOS',
              step: 1
            });
            window.open('https://apps.apple.com/us/app/qcpr/id1042221580', '_blank', 'noopener,noreferrer');
          }
        }]}
        title={t('iOS: Go to App Store to get the latest version of the app', { ns: 'qcprSupport' })}
        action={() => {
          posthog.capture('SUPP-QcprSupport-AppStoreClicked', {
            platform: 'iOS',
            step: 1
          });
          window.open('https://apps.apple.com/us/app/qcpr/id1042221580', '_blank', 'noopener,noreferrer');
        }}
      />

      <HorizontalCard
        actions={[{
          componentType: 'icon',
          icon: <SystemIcons.OpenNewWindow />,
          action: () => {
            posthog.capture('SUPP-QcprSupport-AppStoreClicked', {
              platform: 'Android',
              step: 1
            });
            window.open('https://play.google.com/store/apps/details?id=com.Laerdal.QCPRinstructor&hl=en', '_blank', 'noopener,noreferrer');
          }
        }]}
        title={t('Android: Go to Google Play to get the latest version of the app', { ns: 'qcprSupport' })}
        action={() => {
          posthog.capture('SUPP-QcprSupport-AppStoreClicked', {
            platform: 'Android',
            step: 1
          });
          window.open('https://play.google.com/store/apps/details?id=com.Laerdal.QCPRinstructor&hl=en', '_blank', 'noopener,noreferrer');
        }}
      />

      <Button
        size={Size.Large}
        onClick={handleResolvedStep} >
        {t("It resolved the issue", { ns: 'qcprSupport' })}
      </Button>

      <StepDescription>{t('The issue still persist?', { ns: 'qcprSupport' })}</StepDescription>
      <StepDescription>{t('To help you further, please let us know the app version.', { ns: 'qcprSupport' })}</StepDescription>

      <InputLabel inputId="appVersion" text={t('Add QCPR app version')} size={Size.Small} required={true} />
      <TextField
        id="appVersion"
        data-testId="appVersion"
        placeholder={t('Eg: 0.000', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={version}
        onChange={(value) => setVersion(value)}
      />

      <Button
        size={Size.Large}
        variant='secondary'
        disabled={!version}
        onClick={() => {
          posthog.capture('SUPP-QcprSupport-AppVersionProvided', {
            app_version: version,
            step: 1
          });
          setAppVersion(version);
          onNext();
        }}>
        {t("Continue", { ns: 'qcprSupport' })}
      </Button>

      <GrayBox>
        <ComponentM textStyle={ComponentTextStyle.Bold}>
          {t("Here's how to check", { ns: 'qcprSupport' })}
        </ComponentM>

        <ComponentM textStyle={ComponentTextStyle.Regular}>
          {t("Press menu icon → Side menu opens → see Version at the bottom of menu", { ns: 'qcprSupport' })}
        </ComponentM>

        <img src="/assets/qcprAppVersion.png" style={{ maxWidth: '100%', height: 'auto' }} alt="Instructions to check app version" />

      </GrayBox>
      <StepDescription>{t('Your answers will help our team to assist you.', { ns: 'qcprSupport' })}</StepDescription>
    </StepContainer>
  );
};

export default Step1;
